import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "./Navbar";

const RECIPES = [
  {
    id: 1,
    title: "Perfect Soft-Boiled Eggs",
    description:
      "Learn how to make the perfect soft-boiled eggs for breakfast or brunch.",
    image:
      "https://images.unsplash.com/photo-1518568814500-bf0f8d125f46?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    slug: "perfect-soft-boiled-eggs",
    tags: ["soft-boiled"],
    timerSetting: "6-7 minutes",
  },
  {
    id: 2,
    title: "Jammy Eggs for Ramen",
    description:
      "Create the perfect jammy eggs to top your favorite ramen bowl.",
    image:
      "https://images.unsplash.com/photo-1569718212165-3a8278d5f624?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    slug: "jammy-eggs-for-ramen",
    tags: ["runny-yolks"],
    timerSetting: "6 minutes",
  },
  {
    id: 3,
    title: "Hard-Boiled Eggs for Meal Prep",
    description:
      "Make a batch of hard-boiled eggs for your weekly meal prep routine.",
    image:
      "https://images.unsplash.com/photo-1518568814500-bf0f8d125f46?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    slug: "hard-boiled-eggs-meal-prep",
    tags: ["hard-boiled"],
    timerSetting: "10 minutes",
  },
  {
    id: 4,
    title: "Dippy Eggs with Soldiers",
    description:
      "Classic British breakfast with perfectly runny eggs and toast soldiers.",
    image:
      "https://images.unsplash.com/photo-1518568814500-bf0f8d125f46?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    slug: "dippy-eggs-with-soldiers",
    tags: ["runny-yolks"],
    timerSetting: "3 minutes",
  },
  {
    id: 5,
    title: "Classic Deviled Eggs",
    description:
      "A party favorite with creamy, tangy filling and a sprinkle of paprika.",
    image:
      "https://images.unsplash.com/photo-1580013759032-c96505e24c1f?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    slug: "classic-deviled-eggs",
    tags: ["hard-boiled"],
    timerSetting: "10 minutes",
  },
  {
    id: 6,
    title: "Japanese Egg Sando",
    description:
      "Creamy Japanese-style egg salad sandwich with a perfect balance of flavors.",
    image:
      "https://images.unsplash.com/photo-1528735602780-2552fd46c7af?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    slug: "japanese-egg-sando",
    tags: ["hard-boiled"],
    timerSetting: "10 minutes",
  },
  {
    id: 7,
    title: "Perfect Egg Salad Sandwich",
    description:
      "A classic sandwich with creamy egg salad, perfect for picnics and lunches.",
    image:
      "https://images.unsplash.com/photo-1528735602780-2552fd46c7af?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    slug: "perfect-egg-salad-sandwich",
    tags: ["hard-boiled"],
    timerSetting: "10 minutes",
  },
  {
    id: 8,
    title: "Cobb Salad with Hard-Boiled Eggs",
    description:
      "A hearty salad with hard-boiled eggs, bacon, avocado, and blue cheese.",
    image:
      "https://images.unsplash.com/photo-1540420773420-3366772f4999?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    slug: "cobb-salad-with-hard-boiled-eggs",
    tags: ["hard-boiled"],
    timerSetting: "10 minutes",
  },
  {
    id: 9,
    title: "Scotch Eggs",
    description:
      "A British pub favorite with hard-boiled eggs wrapped in seasoned sausage.",
    image:
      "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    slug: "scotch-eggs",
    tags: ["hard-boiled"],
    timerSetting: "10 minutes",
  },
];

// Get all unique tags from recipes
const ALL_TAGS = [...new Set(RECIPES.flatMap((recipe) => recipe.tags))];

const Recipes = () => {
  const [selectedTags, setSelectedTags] = useState([]);

  // Filter recipes based on selected tags
  const filteredRecipes =
    selectedTags.length > 0
      ? RECIPES.filter((recipe) =>
          selectedTags.every((tag) => recipe.tags.includes(tag))
        )
      : RECIPES;

  const toggleTag = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter((t) => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  // Format tag for display (replace hyphens with spaces and capitalize)
  const formatTag = (tag) => {
    return tag
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
        <header className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Egg Cooking Recipes
          </h1>
          <p className="text-xl text-gray-600">
            Discover delicious ways to cook eggs using our timer
          </p>
        </header>

        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Filter by Egg Style:
          </h3>
          <div className="flex flex-wrap gap-2">
            {ALL_TAGS.map((tag) => (
              <button
                key={tag}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  selectedTags.includes(tag)
                    ? "bg-orange-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
                onClick={() => toggleTag(tag)}
              >
                {formatTag(tag)}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredRecipes.map((recipe) => (
            <Link
              to={`/recipes/${recipe.slug}`}
              className="block"
              key={recipe.id}
            >
              <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg h-full flex flex-col">
                <div
                  className="h-48 bg-cover bg-center"
                  style={{ backgroundImage: `url(${recipe.image})` }}
                ></div>
                <div className="p-6 flex flex-col flex-grow">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    {recipe.title}
                  </h2>
                  <p className="text-gray-600 mb-4 flex-grow">
                    {recipe.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {recipe.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                      >
                        {formatTag(tag)}
                      </span>
                    ))}
                  </div>
                  <span className="text-orange-500 font-medium hover:text-orange-600 transition-colors duration-200">
                    View Recipe
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Recipes;
